import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../config/api.config';

/**
 * Real API function for technical alerts filters with faceted search
 * Calls the backend /api/alerts/filters endpoint
 */
export const fetchTechnicalAlertsFilters = async (clientId, currentFilters = {}) => {
  
  if (!clientId) {
    throw new Error('clientId is required for fetching technical alerts filters');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    
    // Add filter parameters in the format the API expects: filters[key]=value
    Object.entries(currentFilters).forEach(([key, values]) => {
      if (values && Array.isArray(values) && values.length > 0) {
        const backendKey = key === 'route' || key === 'routes' ? 'routes' : key;
        values.forEach(value => {
          params.append(`filters[${backendKey}]`, value);
        });
      }
    });

    const response = await axios.get(
      `${API_BASE_URL}/alerts/filters?${params.toString()}`,
      {
        headers: createAuthHeader(token)
      }
    );

    // Backend returns products as objects with {id, name}, no slots
    return response.data;
  } catch (error) {
    console.error('Error fetching technical alerts filters:', error);
    throw error;
  }
};

/**
 * Real API function for technical alerts with pagination, filtering, and sorting
 * Calls the backend /api/alerts endpoint
 */
export const fetchTechnicalAlerts = async (clientId, filters = {}, sort = {}, page = 1, pageSize = 10) => {
  
  if (!clientId) {
    throw new Error('clientId is required for fetching technical alerts');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    params.append('page', page);
    params.append('pageSize', pageSize);
    
    // Add filter parameters - transform frontend names to backend names
    if (filters.group?.length > 0) {
      filters.group.forEach(v => params.append('filters[groups]', v));
    }
    if (filters.location?.length > 0) {
      filters.location.forEach(v => params.append('filters[locations]', v));
    }
    if (filters.machineId?.length > 0) {
      filters.machineId.forEach(v => params.append('filters[machines]', v));
    }
    if (filters.productName?.length > 0) {
      filters.productName.forEach(v => params.append('filters[products]', v));
    }
    if (filters.sector?.length > 0) {
      filters.sector.forEach(v => params.append('filters[sectors]', v));
    }
    const routeFilters = filters.routes || filters.route;
    if (routeFilters?.length > 0) {
      routeFilters.forEach(v => params.append('filters[routes]', v));
    }
    if (filters.weeksSince?.length > 0) {
      filters.weeksSince.forEach(v => params.append('filters[weeksSince]', v));
    }
    if (filters.alertType?.length > 0) {
      filters.alertType.forEach(v => params.append('filters[alertTypes]', v));
    }
    if (filters.status?.length > 0) {
      filters.status.forEach(v => params.append('filters[statuses]', v));
    }
    
    // Add sort parameters
    if (sort.column) {
      params.append('sortColumn', sort.column);
    }
    if (sort.order) {
      params.append('sortOrder', sort.order);
    }

    const response = await axios.get(
      `${API_BASE_URL}/alerts?${params.toString()}`,
      {
        headers: createAuthHeader(token)
      }
    );

    // Use backend data directly - no need for additional keys
    const dataWithKeys = response.data.data;
    
    return {
      data: dataWithKeys,
      pagination: response.data.pagination,
      totals: response.data.totals, // Pass through totals from backend
      allAlertIds: response.data.data.map(a => a.id) // For bulk operations using real IDs
    };
  } catch (error) {
    console.error('Error fetching technical alerts:', error);
    throw error;
  }
};

/**
 * Vectorized alert update endpoint - handles single or multiple alerts
 * Calls the real backend /api/alerts/update endpoint
 * Backend owns all business logic, frontend just passes through action
 */
export const updateAlerts = async (clientId, alertIds, action, metadata = {}) => {
  
  if (!clientId) {
    throw new Error('clientId is required for updating alerts');
  }

  // Ensure alertIds is always an array and convert to integers
  const rawIds = Array.isArray(alertIds) ? alertIds : [alertIds];
  const ids = rawIds.map(id => {
    const numId = parseInt(id, 10);
    if (isNaN(numId)) {
      throw new Error(`Invalid alert ID: ${id}. Alert IDs must be integers.`);
    }
    return numId;
  });
  
  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await axios.post(
      `${API_BASE_URL}/alerts/update?clientId=${clientId}`,
      {
        alertIds: ids,
        action,
        metadata
      },
      {
        headers: createAuthHeader(token)
      }
    );


    return response.data;
  } catch (error) {
    console.error('Error updating alerts:', error);
    throw error;
  }
};

/**
 * Fetch technical alerts grouped by strategy (store_cluster, machine, product, temporal, location)
 * Calls the backend /api/alerts/groups endpoint
 */
export const fetchAlertGroups = async (clientId, filters = {}, groupBy = 'store_cluster', page = 1, pageSize = 10) => {
  
  if (!clientId) {
    throw new Error('clientId is required for fetching alert groups');
  }

  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    params.append('groupBy', groupBy);
    params.append('page', page);
    params.append('pageSize', pageSize);
    
    // Add filter parameters - reuse same logic as fetchTechnicalAlerts
    if (filters.group?.length > 0) {
      filters.group.forEach(v => params.append('filters[groups]', v));
    }
    if (filters.location?.length > 0) {
      filters.location.forEach(v => params.append('filters[locations]', v));
    }
    if (filters.machineId?.length > 0) {
      filters.machineId.forEach(v => params.append('filters[machines]', v));
    }
    if (filters.productName?.length > 0) {
      filters.productName.forEach(v => params.append('filters[products]', v));
    }
    if (filters.weeksSince?.length > 0) {
      filters.weeksSince.forEach(v => params.append('filters[weeksSince]', v));
    }
    if (filters.alertType?.length > 0) {
      filters.alertType.forEach(v => params.append('filters[alertTypes]', v));
    }
    if (filters.status?.length > 0) {
      filters.status.forEach(v => params.append('filters[statuses]', v));
    }
    // Handle actions filter (maps to status) - same logic as table view
    if (filters.actions?.length > 0) {
      filters.actions.forEach(v => params.append('filters[statuses]', v));
    }
    const groupRouteFilters = filters.routes || filters.route;
    if (groupRouteFilters?.length > 0) {
      groupRouteFilters.forEach(v => params.append('filters[routes]', v));
    }

    // No additional parameters needed - backend always returns all alerts for current page groups

    const response = await axios.get(
      `${API_BASE_URL}/alerts/groups?${params.toString()}`,
      {
        headers: createAuthHeader(token)
      }
    );


    return response.data;
  } catch (error) {
    console.error('Error fetching alert groups:', error);
    throw error;
  }
};
